import { CATEGORY_LABEL, DEFAULT_ALIASES, categoryOf, normalizeTag, type Tag, type TagCategory } from '@photos/shared';

/** Tag registry + aliases with the lookups the UI needs. */
export class TagIndex {
  readonly registry: Map<string, Tag>;
  readonly aliases: Record<string, string>;

  constructor(tags: Tag[] = [], aliases: Record<string, string> = {}) {
    this.registry = new Map(tags.map((t) => [t.name, t]));
    const merged: Record<string, string> = { ...DEFAULT_ALIASES, ...aliases };
    for (const t of tags) for (const a of t.aliases ?? []) merged[a.toLowerCase().replace(/[^a-z0-9]/g, '')] = t.name;
    this.aliases = merged;
  }

  catOf(name: string): TagCategory {
    return categoryOf(name, this.registry);
  }

  labelOf(name: string): string {
    return this.registry.get(name)?.label ?? name;
  }

  categoryLabel(name: string): string {
    return CATEGORY_LABEL[this.catOf(name)];
  }

  /** Canonical tag for user input ("#Summer Retreat" → "summer-retreat", "YA" → "young-adults"). */
  normalize(input: string): string | null {
    return normalizeTag(input, this.aliases);
  }

  /** The alias target for a query, if the query is an alias. */
  aliasFor(query: string): string | undefined {
    const raw = query.trim().toLowerCase().replace(/^#+/, '').replace(/[^a-z0-9]/g, '');
    return raw ? this.aliases[raw] : undefined;
  }

  /** Aliases that point at `name`. */
  aliasesOf(name: string): string[] {
    return Object.entries(this.aliases)
      .filter(([, v]) => v === name)
      .map(([k]) => k);
  }

  /**
   * "Event" label for a set of tags, as uploads are auto-named: first event tag label + year.
   * Returns '' when the photo has no event tag.
   */
  eventLabel(tags: readonly string[]): string {
    const ev = tags.find((t) => this.catOf(t) === 'event');
    if (!ev) return '';
    const year = tags.find((t) => this.catOf(t) === 'year');
    return `${this.labelOf(ev)}${year ? ` ${year}` : ''}`;
  }
}

export interface Suggestions {
  keys: string[];
  alias?: string;
}

/**
 * Autocomplete for the search box (prototype `suggest`): tags that occur in the current results, minus active ones,
 * matching the query by name, label or alias. The alias match comes first, then category tags, then by count.
 */
export function suggestTags(
  query: string,
  counts: ReadonlyMap<string, number>,
  active: readonly string[],
  index: TagIndex,
  limit = 8,
): Suggestions {
  const q = query.trim().toLowerCase().replace(/^#/, '');
  const alias = q ? index.aliasFor(q) : undefined;
  const dashed = q.replace(/\s+/g, '-');
  const normalized = q ? index.normalize(q) : null;
  let keys = [...counts.keys()].filter((t) => !active.includes(t));
  if (q)
    keys = keys.filter(
      (t) =>
        t.includes(dashed) ||
        (normalized !== null && t.includes(normalized)) ||
        index.labelOf(t).toLowerCase().includes(q) ||
        t === alias,
    );
  const isCat = (t: string) => (index.catOf(t) !== '' ? 1 : 0);
  keys.sort(
    (a, b) =>
      Number(b === alias) - Number(a === alias) ||
      isCat(b) - isCat(a) ||
      (counts.get(b) ?? 0) - (counts.get(a) ?? 0) ||
      a.localeCompare(b),
  );
  return { keys: keys.slice(0, limit), alias };
}
